import React, { useEffect, useMemo, useState, useRef } from "react";
import placeholder from "../assets/Car-place-holder-PNG.png";
import { infoIcons } from "../assets/VehicleDetailsIcon";
import { carIcon } from "../assets/VehicleDetailsIcon";
import { InfoItem } from "../assets/VehicleDetailsIcon";
import { bidIcon } from "../assets/VehicleDetailsIcon";
import { auctionIcon } from "../assets/VehicleDetailsIcon";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api.ts";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import { formatPrice, maskVinNumber } from "../utils/functions";

import BidInformation from "./BidInformation";
import {
  Calendar,
  Clock,
  MapPin,
  Car,
  ShieldCheck,
  CircleDollarSign,
  Timer,
  Camera,
  X,
  ChevronLeft,
  ChevronRight,
  Download,
} from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { generateVehiclePDF } from "./VehiclePDFGenerator";
const bidInfo = {
  eligibilityStatus: "Not Eligible",
  startingBid: "USD 30,000",
  currentBid: "Not Available",
  yourBidStatus: "You haven't Bid",
  preBid: "30,500",
  timeLeft: "04 : 39 : 58",
};

const auctionInfo = {
  sellerName: "Online Action (Friday)",
  saleLocation: "Sky Auto Auction, Sharjah",
  saleDate: "4, Jul 2025",
  laneRun: "A-152",
  timeLeft: "04 : 39 : 58",
  lastUpdated: "30/08/2025, 04:59pm",
};

const SectionHeader = ({ title, icon }) => (
  <h2 className="bg-card text-white p-[10px] m-[-15px] mb-[10px] border-b-0 font-bold flex items-center gap-1 text-lg md:text-[18px]">
    <span>{icon}</span>
    {title}
  </h2>
);

const VehicleDetail = ({ carDetail, totalAuctionVehicles }) => {

  const { language, translate } = useLanguage();
  const navigate = useNavigate();
  // console.log("Vehicle Detail Data:", carDetail);
  const isRTL = language === "ar";

  // Inject the countdown timer styles

  // Prebid countdown timer state
  const [prebidTimeLeft, setPrebidTimeLeft] = useState(() => {
    if (
      !carDetail?.auctionLanesVehicles?.[0]?.auctionLane?.auction?.preBidEndDate
    )
      return 0;
    const start = new Date(
      carDetail.auctionLanesVehicles[0].auctionLane.auction.preBidEndDate,
    );
    const now = new Date();
    return Math.max(0, start.getTime() - now.getTime());
  });

  // Auction start countdown timer state
  const [auctionTimeLeft, setAuctionTimeLeft] = useState(() => {
    if (!carDetail?.auctionLanesVehicles?.[0]?.auctionLane?.auction?.startDate)
      return 0;
    const start = new Date(
      carDetail.auctionLanesVehicles[0].auctionLane.auction.startDate,
    );
    const now = new Date();
    return Math.max(0, start.getTime() - now.getTime());
  });

  // State variables for prebid countdown display
  const [prebidDays, setPrebidDays] = useState("00");
  const [prebidHours, setPrebidHours] = useState("00");
  const [prebidMinutes, setPrebidMinutes] = useState("00");
  const [prebidSeconds, setPrebidSeconds] = useState("00");

  // State variables for auction start countdown display
  const [auctionDays, setAuctionDays] = useState("00");
  const [auctionHours, setAuctionHours] = useState("00");
  const [auctionMinutes, setAuctionMinutes] = useState("00");
  const [auctionSeconds, setAuctionSeconds] = useState("00");

  const { isAuthenticated, user } = useAuth();
  const { showSuccess, showError } = useToast();
  const visitingfrom = new URLSearchParams(window.location.search).get(
    "visitingfrom",
  );

  // Build an array of URL strings from mixed API shapes
  const imageUrls = useMemo(() => {
    const urls = (carDetail?.images || [])
      .map((img) =>
        typeof img === "string"
          ? img
          : img?.imageUrl || img?.url || img?.path || img?.src || "",
      )
      .filter(Boolean);

    return urls.length > 0 ? urls : [placeholder];
  }, [carDetail]);

  const [selectedImage, setSelectedImage] = useState(
    imageUrls[0] || placeholder,
  );
  const [isImageZoomed, setIsImageZoomed] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(2);
  const [imageTransformOrigin, setImageTransformOrigin] =
    useState("center center");
  const imageWrapperRef = useRef(null);
  const [bidAmount, setBidAmount] = useState("");
  const [bidIncrement, setBidIncrement] = useState(100); // Default bid increment
  const [isSubmittingBid, setIsSubmittingBid] = useState(false);
  const [bidMessage, setBidMessage] = useState("");

  // Gallery modal state
  const [showGallery, setShowGallery] = useState(false);
  const [currentGalleryIndex, setCurrentGalleryIndex] = useState(0);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  // Update selected image when new data arrives
  useEffect(() => {
    if (imageUrls.length) setSelectedImage(imageUrls[0]);
    else setSelectedImage(placeholder);
  }, [imageUrls]);

  // Handle Zoom Wheel Event
  useEffect(() => {
    const wrapper = imageWrapperRef.current;
    if (!wrapper) return;

    const handleWheel = (e) => {
      if (isImageZoomed) {
        e.preventDefault();
        const delta = e.deltaY < 0 ? 0.2 : -0.2;
        setZoomLevel((prev) => Math.min(Math.max(1, prev + delta), 5));
      }
    };

    wrapper.addEventListener("wheel", handleWheel, { passive: false });
    return () => {
      wrapper.removeEventListener("wheel", handleWheel);
    };
  }, [isImageZoomed]);

  // Calculate initial bid amount when carDetail changes
  useEffect(() => {
    if (carDetail) {
      // Get bid increment from carDetail
      const increment = parseFloat(carDetail.bidIncrement) || 100; // Default to 100 if not provided

      // Get current bid amount (latest bid or starting bid)
      const currentBid = parseFloat(carDetail.latestBid) || 0;
      const startingBid = parseFloat(carDetail.startingBid) || 0;
      const baseBidAmount = currentBid > 0 ? currentBid : startingBid;

      // Calculate next bid amount: current bid + increment

      const nextBidAmount = baseBidAmount + increment;

      // Update state
      setBidIncrement(increment);
      setBidAmount(nextBidAmount.toString());
    }
  }, [carDetail]);

  // Timer effect for prebid countdown
  useEffect(() => {
    if (

      carDetail?.auctionLanesVehicles?.[0]?.auctionLane?.auction?.preBidEndDate
    ) {
      console.log("preBid date", carDetail?.auctionLanesVehicles?.[0]?.auctionLane?.auction?.preBidEndDate)
      const updatePrebidTimer = () => {
        const prebidEndDate = new Date(
          carDetail.auctionLanesVehicles[0].auctionLane.auction.preBidEndDate,
        );
        const now = new Date();
        const timeDiff = prebidEndDate.getTime() - now.getTime();
        setPrebidTimeLeft(Math.max(0, timeDiff));
      };

      // Update immediately
      updatePrebidTimer();

      // Update every second
      const timer = setInterval(updatePrebidTimer, 1000);

      // Cleanup timer on unmount
      return () => clearInterval(timer);
    }
  }, [carDetail]);

  // Timer effect for auction start countdown
  useEffect(() => {
    if (carDetail?.auctionLanesVehicles?.[0]?.auctionLane?.auction?.startDate) {
      const updateAuctionTimer = () => {
        const auctionStartDate = new Date(
          carDetail.auctionLanesVehicles[0].auctionLane.auction.startDate,
        );
        const now = new Date();
        const timeDiff = auctionStartDate.getTime() - now.getTime();
        setAuctionTimeLeft(Math.max(0, timeDiff));
      };

      // Update immediately
      updateAuctionTimer();

      // Update every second
      const timer = setInterval(updateAuctionTimer, 1000);

      // Cleanup timer on unmount
      return () => clearInterval(timer);
    }
  }, [carDetail]);

  // Effect for updating the prebid countdown timer display
  useEffect(() => {
    if (prebidTimeLeft > 0) {
      // Calculate days, hours, minutes, seconds
      const calculatedDays = Math.floor(prebidTimeLeft / (1000 * 60 * 60 * 24));
      const calculatedHours = Math.floor(
        (prebidTimeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
      );
      const calculatedMinutes = Math.floor(
        (prebidTimeLeft % (1000 * 60 * 60)) / (1000 * 60),
      );
      const calculatedSeconds = Math.floor(
        (prebidTimeLeft % (1000 * 60)) / 1000,
      );

      // Format values with leading zeros and update state
      setPrebidDays(calculatedDays.toString().padStart(2, "0"));
      setPrebidHours(calculatedHours.toString().padStart(2, "0"));
      setPrebidMinutes(calculatedMinutes.toString().padStart(2, "0"));
      setPrebidSeconds(calculatedSeconds.toString().padStart(2, "0"));
    } else {
      // Set to 00 when countdown reaches zero
      setPrebidDays("00");
      setPrebidHours("00");
      setPrebidMinutes("00");
      setPrebidSeconds("00");
    }
  }, [prebidTimeLeft]);

  // Effect for updating the auction start countdown timer display
  useEffect(() => {
    if (auctionTimeLeft > 0) {
      // Calculate days, hours, minutes, seconds
      const calculatedDays = Math.floor(
        auctionTimeLeft / (1000 * 60 * 60 * 24),
      );
      const calculatedHours = Math.floor(
        (auctionTimeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60),
      );
      const calculatedMinutes = Math.floor(
        (auctionTimeLeft % (1000 * 60 * 60)) / (1000 * 60),
      );
      const calculatedSeconds = Math.floor(
        (auctionTimeLeft % (1000 * 60)) / 1000,
      );

      // Format values with leading zeros and update state
      setAuctionDays(calculatedDays.toString().padStart(2, "0"));
      setAuctionHours(calculatedHours.toString().padStart(2, "0"));
      setAuctionMinutes(calculatedMinutes.toString().padStart(2, "0"));
      setAuctionSeconds(calculatedSeconds.toString().padStart(2, "0"));
    } else {
      // Set to 00 when countdown reaches zero
      setAuctionDays("00");
      setAuctionHours("00");
      setAuctionMinutes("00");
      setAuctionSeconds("00");
    }
  }, [auctionTimeLeft]);

  // Gallery modal functions
  const openGallery = (index = 0) => {
    setCurrentGalleryIndex(index);
    setShowGallery(true);
  };

  const closeGallery = () => {
    setShowGallery(false);
  };

  const nextImage = () => {
    setCurrentGalleryIndex((prev) => (prev + 1) % imageUrls.length);
  };

  const previousImage = () => {
    setCurrentGalleryIndex(
      (prev) => (prev - 1 + imageUrls.length) % imageUrls.length,
    );
  };


  const handleViewCatalog = (auctionLane) => {
    console.log("Auction object:", auctionLane?.auction);
    console.log("Auction Lane object:", auctionLane);

    const auctionId = auctionLane?.auctionId;
    console.log("Auction ID used for navigation:", auctionId);

    if (!auctionId) {
      console.error("Auction ID is missing!");
      return;
    }

    navigate(`/vehicles?auctionId=${auctionId}`);
  };

  const downloadAllImages = async () => {
    // Collect real image URLs (skip placeholder)
    const realImages = imageUrls.filter(
      (u) => u && !u.includes("Car-place-holder")
    );
    if (realImages.length === 0) {
      showError("No images available to download.");
      return;
    }

    // ── Strategy 1: backend proxy via api instance (avoids browser CORS) ─────
    const tryViaProxy = async (url) => {
      const response = await api.get("/products/image-proxy", {
        params: { url },
        responseType: "arraybuffer",
        timeout: 15000,
      });
      if (response.status === 200 && response.data?.byteLength > 0) {
        return response.data;
      }
      throw new Error("Proxy returned empty or bad response");
    };

    // ── Strategy 2: direct CORS fetch ────────────────────────────────────────
    const tryDirectFetch = async (url) => {
      const response = await fetch(url, { mode: "cors" });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return await response.arrayBuffer();
    };

    const getExt = (url) => {
      const raw = url.split(".").pop().split("?")[0].toLowerCase();
      return ["jpg", "jpeg", "png", "gif", "webp", "avif"].includes(raw)
        ? raw
        : "jpg";
    };

    // ── Try ZIP approach first ────────────────────────────────────────────────
    // try {
    //   const zip = new JSZip();
    //   const folder = zip.folder("vehicle-images");
    //   let added = 0;

    //   for (let i = 0; i < realImages.length; i++) {
    //     const url = realImages[i];
    //     try {
    //       let buffer;
    //       try {
    //         buffer = await tryViaProxy(url);
    //       } catch {
    //         buffer = await tryDirectFetch(url);
    //       }
    //       folder.file(`image-${i + 1}.${getExt(url)}`, buffer);
    //       added++;
    //     } catch (err) {
    //       console.warn(`Could not fetch image ${i + 1}:`, err.message);
    //     }
    //   }

    //   if (added > 0) {
    //     const content = await zip.generateAsync({ type: "blob" });
    //     saveAs(content, `vehicle-${carDetail?.lotNumber || "images"}.zip`);
    //     return;
    //   }
    // } catch (err) {
    //   console.warn("ZIP creation failed:", err.message);
    // }

    try {
      const zip = new JSZip(); // No subfolder

      let added = 0;

      for (let i = 0; i < realImages.length; i++) {
        const url = realImages[i];
        try {
          let buffer;
          try {
            buffer = await tryViaProxy(url);
          } catch {
            buffer = await tryDirectFetch(url);
          }
          // Add images directly to the root of the ZIP
          zip.file(`image-${i + 1}.${getExt(url)}`, buffer);
          added++;
        } catch (err) {
          console.warn(`Could not fetch image ${i + 1}:`, err.message);
        }
      }

      if (added > 0) {
        const content = await zip.generateAsync({ type: "blob" });
        saveAs(content, `vehicle-${carDetail?.lotNumber || "images"}.zip`);
        return;
      }
    } catch (err) {
      console.warn("ZIP creation failed:", err.message);
    }

    // ── Fallback: open each image URL in a new tab ────────────────────────────
    // This always works — user can right-click → Save Image
    showError(
      `Cannot bundle images (CORS restriction). Opening ${realImages.length} image(s) in new tabs — right-click each to save.`
    );
    realImages.forEach((url, i) => {
      setTimeout(() => window.open(url, "_blank", "noopener,noreferrer"), i * 300);
    });
  };


  // Keyboard navigation for gallery
  useEffect(() => {
    if (!showGallery) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") closeGallery();
      if (e.key === "ArrowLeft") previousImage();
      if (e.key === "ArrowRight") nextImage();
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showGallery, imageUrls.length]);

  // Helper function to get minimum bid amount
  const getMinBidAmount = () => {
    if (!carDetail) return 0;
    const currentBid = parseFloat(carDetail.latestBid) || 0;
    const startingBid = parseFloat(carDetail.startingBid) || 0;
    return currentBid > 0 ? currentBid : startingBid;
  };

  // Helper function to get next minimum bid amount
  const getNextMinBidAmount = () => {
    const minBid = getMinBidAmount();
    const increment = parseFloat(carDetail?.bidIncrement) || 100;
    return minBid + increment;
  };

  if (!carDetail) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <p>Loading vehicle details...</p>
      </div>
    );
  }

  // Check if vehicle data is valid and not empty
  if (!carDetail.id && !carDetail.lotNumber && !carDetail.vinNumber) {
    return (
      <div className="max-w-7xl mx-auto p-6">
        <div className="text-center">
          <div className="mb-6">
            <div className="w-24 h-24 mx-auto bg-red-100 rounded-full flex items-center justify-center">
              <svg
                className="w-12 h-12 text-red-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
          </div>
          <h1 className="text-2xl font-bold text-gray-800 mb-4">
            Vehicle Not Found
          </h1>
          <p className="text-gray-600 mb-6">
            The vehicle you're looking for could not be found or may have been
            removed.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => window.history.back()}
              className="bg-gray-600 text-white px-6 py-3 rounded-lg hover:bg-gray-700 transition-colors font-medium"
            >
              ← Go Back
            </button>
            <a
              href="/search"
              className="bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600 transition-colors font-medium text-center"
            >
              Browse Other Vehicles
            </a>
          </div>
        </div>
      </div>
    );
  }

  const carInfoItems = [
    ["Lot Number", carDetail?.lotNumber],
    [
      "VIN",
      carDetail?.vinNumber && (
        <span style={{ whiteSpace: "nowrap", fontFamily: "monospace" }}>
          {maskVinNumber(carDetail?.vinNumber, 8)}
        </span>
      ),
    ],
    ["Highlights", carDetail?.Highlight?.name],
    ["Year", carDetail?.year],
    ["Make", carDetail?.Make],
    ["Model", carDetail?.MakeModel],

    ["Variant", carDetail?.variant],
    ["Milage", carDetail?.mileage + " Mi (Not Actual)"],
    ["Transmission", carDetail?.TransmissionType],
    ["Drive Type", carDetail?.DriveLineType],
    ["Vehicle Type", carDetail?.BodyType],
    ["Seat Type", carDetail?.seatType],
    ["Specification", carDetail?.specifications || carDetail?.specification],

    ["Fuel", carDetail?.fuelType],
    ["Engine Type", carDetail?.engine],
    ["Cylinders", carDetail?.cylinder],
    [
      "EXT / INT Color",
      <div className="flex items-center gap-2">
        {/* Exterior Swatch & Name */}
        <div className="flex items-center gap-1">
          {/* <span
          className="inline-block w-3 h-3 rounded-full border border-gray-500"
          style={{ background: carDetail?.ExteriorColor?.color || 'transparent' }}
        /> */}
          <span>{carDetail?.ExteriorColor?.name || "-"}</span>
        </div>

        <span className="text-description">/</span>

        {/* Interior Swatch & Name */}
        <div className="flex items-center gap-1">
          {/* <span
          className="inline-block w-3 h-3 rounded-full border border-gray-500"
          style={{ background: carDetail?.InteriorColor?.color || 'transparent' }}
        /> */}
          <span>{carDetail?.InteriorColor?.name || "-"}</span>
        </div>
      </div>,
    ],
    ["Seat Type", carDetail?.seatType],

    // ["Primary Damage", carDetail?.primaryDamageType],
    // ["Secondary Damage", carDetail?.secondaryDamageType],
    //   ["Keys", carDetail?.keys ? "Yes" : "No"],

    // [
    //   "Color (Interior)",
    //   carDetail?.InteriorColor?.color?.startsWith("#") ? (
    //     <div className="flex items-center gap-2">
    //       <span
    //         style={{
    //           display: "inline-block",
    //           width: 24,
    //           height: 24,
    //           background: carDetail?.InteriorColor?.color,
    //           border: "1px solid #ccc",
    //           borderRadius: "50%",
    //           verticalAlign: "middle",
    //         }}
    //         title={carDetail?.InteriorColor?.name}
    //       />
    //       <span className="text-gray-700 font-medium">
    //         {carDetail?.InteriorColor?.name || "-"}
    //       </span>
    //     </div>
    //   ) : (
    //     <span className="text-gray-700">-</span>
    //   ),
    // ],
    // [
    //   "Color (Exterior)",
    //   carDetail?.ExteriorColor?.color?.startsWith("#") ? (
    //     <div className="flex items-center gap-2">
    //       <span
    //         style={{
    //           display: "inline-block",
    //           width: 24,
    //           height: 24,
    //           background: carDetail?.ExteriorColor?.color,
    //           border: "1px solid #ccc",
    //           borderRadius: "50%",
    //           verticalAlign: "middle",
    //         }}
    //         title={carDetail?.ExteriorColor?.name}
    //       />
    //       <span className="text-gray-700 font-medium">
    //         {carDetail?.ExteriorColor?.name || "-"}
    //       </span>
    //     </div>
    //   ) : (
    //     <span className="text-gray-700">-</span>
    //   ),
    // ],
  ].filter(
    ([label, value]) => value !== null && value !== undefined && value !== "",
  );

  const bidInfoItems = [
    ["Eligibility Status", bidInfo.eligibilityStatus],
    ["Starting Bid", carDetail.startingBid],
    ["Current Bid", bidInfo.latestBid],
    ["Your Bid Status", bidInfo.yourBidStatus],
    ["Time Left", `⏱️ ${bidInfo.timeLeft}`],
    ["Pre-Bid", bidInfo.preBid],
  ];

  const auctionInfoItems = [
    [
      "Auction Name",
      carDetail?.auctionLanesVehicles?.[0]?.auctionLane?.auction?.name || "",
    ],
    [
      "Lane",
      carDetail?.auctionLanesVehicles?.[0]?.auctionLane?.lane?.name ||
      carDetail?.auctionLanesVehicles?.[0]?.auctionLane?.laneId ||
      "",
    ],
    [
      "Run #",
      carDetail?.auctionLanesVehicles?.[0]?.auctionLane?.[0]?.runNo ||
      carDetail?.auctionLanesVehicles?.[0]?.runNo ||
      "",
    ],
    [
      "Auction Date",
      carDetail?.auctionLanesVehicles?.[0]?.auctionLane?.auction?.startDate
        ? new Date(
          carDetail.auctionLanesVehicles[0].auctionLane.auction.startDate,
        ).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
        : "",
    ],
    [
      "Auction Time",
      carDetail?.auctionLanesVehicles?.[0]?.auctionLane?.auction?.startDate
        ? new Date(
          carDetail.auctionLanesVehicles[0].auctionLane.auction.startDate,
        ).toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          hour12: true,
        })
        : "",
    ],

    [
      "Sale Location",
      carDetail?.auctionLanesVehicles?.[0]?.auctionLane?.auction?.location?.name ||
      auctionInfo.saleLocation ||
      "",
    ],
  ];
  const handleBidChange = (amount) => {
    setBidAmount(amount);
  };

  const handlePreBidSubmit = async () => {
    if (!isAuthenticated) {
      showError("You must be logged in to place a pre-bid");
      return;
    }

    // Check if pre-bidding is still open
    if (prebidTimeLeft <= 0) {
      showError("Pre-bidding has closed for this auction");
      return;
    }

    // Check if user is authenticated

    // Check if user is a buyer by checking if buyer property exists and seller is null
    const isBuyer = user?.buyer && !user?.seller;

    if (!isBuyer) {
      showError("Only buyers can place pre-bids");
      return;
    }

    if (!carDetail?.auctionLanesVehicles?.[0]?.auctionLane?.id) {
      showError("Error: Auction lane information not available");
      return;
    }

    if (!carDetail?.id) {
      showError("Error: Vehicle information not available");
      return;
    }

    if (!bidAmount || isNaN(bidAmount) || bidAmount <= 0) {
      showError("Error: Please enter a valid bid amount");
      return;
    }

    // Check if bid amount is at least the starting bid
    if (
      carDetail.startingBid &&
      parseFloat(bidAmount) < parseFloat(carDetail.startingBid)
    ) {
      const startingBidValue = carDetail.startingBid;
      const formattedStartingBid =
        typeof startingBidValue === "number"
          ? startingBidValue.toLocaleString("en-US", {
            maximumFractionDigits: 0,
          })
          : startingBidValue;
      showError(`Error: Bid amount must be at least ${formattedStartingBid}`);
      return;
    }

    setIsSubmittingBid(true);
    setBidMessage("");

    try {
      const response = await api.post("/bidding/prebid", {
        auctionLaneId: carDetail.auctionLanesVehicles[0].auctionLane.id,
        vehicleId: carDetail.id,
        amount: parseFloat(bidAmount),
      });
      if (response.data.success) {
        showSuccess("Pre-bid submitted successfully!");
        setBidMessage(""); // Clear any existing bid message

        // Calculate next bid amount
        const currentBid = parseFloat(bidAmount);
        const increment = parseFloat(carDetail.bidIncrement) || 100;
        const nextBidAmount = currentBid + increment;
        setBidAmount(nextBidAmount.toString());

        // Update the carDetail object with the new bid amount and set current user as highest bidder
        carDetail.latestBid = currentBid.toString();
        carDetail.highestBidderId = user?.id; // Set current user as highest bidder

        // Since we can't directly update the DOM with a React component,
        // we'll force a re-render by updating the state
        // This will trigger a re-render of the component with the updated carDetail.latestBid
        setBidAmount((prevBidAmount) => {
          // Use a state update to force a re-render
          return prevBidAmount;
        });

        // Refresh the vehicle details in the background
        if (carDetail?.id) {
          api
            .get(`/products/${carDetail.id}`)
            .catch((error) =>
              console.error("Error refreshing vehicle data:", error),
            );
        }
      } else {
        showError(response.data.message || "Failed to submit pre-bid");
      }
    } catch (error) {
      console.error("Pre-bid submission error:", error);
      showError(
        error.response?.data?.message ||
        "Failed to submit pre-bid. Please try again.",
      );
    } finally {
      setIsSubmittingBid(false);
    }
  };

  const handleBuyNowSubmit = async () => {
    // Check if user is authenticated
    if (!isAuthenticated) {
      showError("You must be logged in to purchase");
      return;
    }

    // Check if user is a buyer
    const isBuyer = user?.buyer && !user?.seller;

    if (!isBuyer) {
      showError("Only buyers can purchase vehicles");
      return;
    }

    if (!carDetail?.id) {
      showError("Error: Vehicle information not available");
      return;
    }

    if (!carDetail?.buyNowPrice || carDetail.buyNowPrice <= 0) {
      showError("Error: Buy now price not available");
      return;
    }

    // Check if buy now is still available (check buyNowEndTime if it exists)
    if (carDetail?.buyNowEndTime) {
      const endTime = new Date(carDetail.buyNowEndTime);
      const now = new Date();
      if (now > endTime) {
        showError("Buy now period has expired");
        return;
      }
    }

    setIsSubmittingBid(true);
    setBidMessage("");

    try {
      const response = await api.post("/bidding/marksoldfrombuynow", {
        vehicleId: carDetail.id,
        buyerId: user?.buyer?.id,
        soldPrice: carDetail.buyNowPrice,
      });

      if (response.data.success) {
        showSuccess("Purchase successful! Vehicle has been marked as sold.");

        setBidMessage("Purchase successful! Vehicle has been marked as sold.");

        // Update the vehicle data
        carDetail.soldPrice = carDetail.buyNowPrice;
        carDetail.soldTo = user?.id;
        carDetail.ProductStatus = "Sold";

        // Refresh the vehicle details in the background
        if (carDetail?.id) {
          api
            .get(`/products/${carDetail.id}`)
            .catch((error) =>
              console.error("Error refreshing vehicle data:", error),
            );
        }
      } else {
        showError(response.data.message || "Failed to complete purchase");
      }
    } catch (error) {
      console.error("Buy now purchase error:", error);
      showError(
        error.response?.data?.message ||
        "Failed to complete purchase. Please try again.",
      );
    } finally {
      setIsSubmittingBid(false);
    }
  };

  return (
    <>
      {visitingfrom && visitingfrom === "livebid" && (
        <Link
          className="text-primary underline pl-4"
          to={`/livebid/${carDetail?.auctionLanesVehicles?.[0]?.auctionLane?.auctionId}/${carDetail?.auctionLanesVehicles?.[0]?.auctionLane?.id}`}
        >
          Go to Auction
        </Link>
      )}

      {/* Breadcrumb Section to span full width above columns */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-12 max-w-7xl pt-4">
        <div className="flex items-center gap-2 text-sm text-description px-2">
          <Link to="/" className="hover:text-white transition-colors">
            {translate("Home")}
          </Link>
          <span>/</span>
          <Link to="/vehicles" className="hover:text-white transition-colors">
            {translate("Vehicles")}
          </Link>
          <span>/</span>
          <span className="text-white font-medium truncate">
            {carDetail?.name || translate("Vehicle Details")}
          </span>
        </div>
      </div>

      {/* <div className="container mx-auto px-4 sm:px-6 lg:px-12 max-w-7xl grid grid-cols-1 lg:grid-cols-4 gap-6 bg-page"> */}
      <div className="container mx-auto px-4 sm:px-6 lg:px-12 max-w-7xl grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6">
        <div className="lg:col-span-8 flex flex-col gap-4 lg:gap-6 p-0 lg:p-2">
          {/* Car Image Container */}
          <div className="w-full shadow-md rounded-xl overflow-hidden">
            {/* Main Image Wrapper */}
            <div
              ref={imageWrapperRef}
              className={`relative overflow-hidden rounded-xl ${isImageZoomed ? 'cursor-zoom-out' : 'cursor-zoom-in'}`} // Added rounded-t-xl to match container top
              onClick={() => {
                setIsImageZoomed(!isImageZoomed);
                if (isImageZoomed) setZoomLevel(2);
              }}
              onMouseMove={(e) => {
                if (isImageZoomed) {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const x = ((e.clientX - rect.left) / rect.width) * 100;
                  const y = ((e.clientY - rect.top) / rect.height) * 100;
                  setImageTransformOrigin(`${x}% ${y}%`);
                }
              }}
            >
              {/* Zoom Controls Overlay */}
              {isImageZoomed && (
                <div 
                  className="absolute top-4 right-4 flex flex-col gap-2 z-40 bg-black/60 rounded-xl p-2"
                  onClick={(e) => e.stopPropagation()} // Prevent toggling zoom off
                >
                  <button 
                    onClick={() => setZoomLevel((prev) => Math.min(prev + 0.5, 5))}
                    className="text-white hover:text-primary p-1 bg-black/40 rounded-full transition-colors"
                    title="Zoom In"
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="12" y1="5" x2="12" y2="19"></line>
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                  </button>
                  <button 
                    onClick={() => setZoomLevel((prev) => Math.max(prev - 0.5, 1))}
                    className="text-white hover:text-primary p-1 bg-black/40 rounded-full transition-colors"
                    title="Zoom Out"
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="5" y1="12" x2="19" y2="12"></line>
                    </svg>
                  </button>
                </div>
              )}

              <img
                src={selectedImage}
                alt="car"
                className="object-cover w-full h-full transition-transform duration-200 ease-out rounded-t-xl" // Ensure image itself is rounded
                style={{
                  transform: isImageZoomed ? `scale(${zoomLevel})` : "scale(1)",
                  transformOrigin: imageTransformOrigin,
                }}
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = placeholder;
                }}
              />

              {/* Highlights Tags */}
              {(() => {
                // Check multiple possible highlight property structures
                const highlightData =
                  carDetail?.Highlight || carDetail?.highlight;

                if (!highlightData) return null;

                const highlights = [
                  {
                    name: highlightData?.name || highlightData,
                    bgColor:
                      carDetail?.highlightBgColor ||
                      highlightData?.bgColor ||
                      "#135E9F",
                    textColor:
                      carDetail?.highlightTextColor ||
                      highlightData?.textColor ||
                      "#ffffff",
                  },
                ];

                return (
                  <div
                    className={`absolute top-2 left-2 px-2 py-1 flex flex-col gap-1 z-10`}
                  >
                    {highlights.slice(0, 3).map((highlight, index) => {
                      const isObject =
                        typeof highlight === "object" && highlight !== null;
                      const name = isObject ? highlight.name : highlight;
                      const bgColor = isObject ? highlight.bgColor : "#135E9F";
                      const textColor = isObject
                        ? highlight.textColor
                        : "#ffffff";

                      return (
                        <div
                          key={index}
                          className="px-3 py-1.5 rounded-lg text-[10px] font-bold text-center shadow-lg uppercase tracking-wider"
                          style={{
                            backgroundColor: bgColor,
                            color: textColor,
                          }}
                        >
                          {name}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}

              <div className="absolute bottom-4 right-4 flex items-center gap-2 z-30">
                {/* Gallery Button */}
                <button
                  onClick={() => openGallery()}
                  className="bg-black/70 text-white px-5 py-2.5 rounded-lg shadow-xl hover:bg-page transition-all active:scale-95 uppercase tracking-wide flex items-center gap-2"
                >
                  <Camera size={32} className="text-white" />
                  <span className="text-[20px]">{imageUrls.length}</span>
                </button>
                {/* Download All Button */}
                <button
                  onClick={downloadAllImages}
                  className="bg-primary text-white px-5 py-2.5 rounded-lg hover:bg-primary-dark transition-all flex items-center gap-2"
                >
                  <Download size={30} />
                </button>
              </div>

              {/* {imageUrls.length > 1 && (
                <>
                  {/* Previous Button
                  <button
                    className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/70 hover:bg-black/60 rounded-full shadow p-2 transition-colors"
                    onClick={() => {
                      const idx = imageUrls.indexOf(selectedImage);
                      setSelectedImage(imageUrls[(idx - 1 + imageUrls.length) % imageUrls.length]);
                    }}
                    aria-label="Previous Image"
                  >
                    <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
                      <path d="M15 6l-6 6 6 6" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>

                  {/* Next Button 
                  <button
                    className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/30 hover:bg-black/50 rounded-full shadow p-2 transition-colors"
                    onClick={() => {
                      const idx = imageUrls.indexOf(selectedImage);
                      setSelectedImage(imageUrls[(idx + 1) % imageUrls.length]);
                    }}
                    aria-label="Next Image"
                  >
                    <svg width="24" height="24" fill="none" viewBox="0 0 24 24">
                      <path d="M9 6l6 6-6 6" stroke="#ffffff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </button>
                </>
              )} */}
            </div>

            {/* Thumbnail Images Section */}
            {/* Thumbnail Images Section */}
            <div className="grid grid-cols-3 md:grid-cols-6 h-auto max-h-[211px] overflow-y-auto gap-3 pt-3 pb-3">
              {imageUrls.length > 0 ? (
                imageUrls.map((img, index) => (
                  <img
                    key={index}
                    src={img || placeholder} // 1. Check if string is empty
                    alt={`thumb-${index}`}
                    onClick={() => setSelectedImage(img)}
                    className={`h-[80px] w-full object-cover cursor-pointer border-2 rounded-lg transition-all ${selectedImage === img
                      ? "border-primary scale-95"
                      : "border-transparent hover:border-gray-400"
                      }`}
                    // 2. Handle broken/unreadable links
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = placeholder;
                    }}
                  />
                ))
              ) : (
                /* 3. Handle empty array case */
                <img
                  src={placeholder}
                  alt="placeholder"
                  className="h-[100px] w-full object-cover border-2 border-transparent rounded-lg"
                />
              )}
            </div>
          </div>

          <div className="bg-card border border-default rounded-lg shadow-md overflow-hidden">
            {/* Card Header */}
            <div className="px-4 py-3 border-b border-default flex justify-between items-center">
              <h2 className="text-white font-semibold text-sm uppercase tracking-wider">
                {translate("vehicleDetail.specification")}
              </h2>
              <button
                disabled={downloadingPdf}
                onClick={async () => {
                  setDownloadingPdf(true);
                  try {
                    await generateVehiclePDF(
                      carDetail,
                      carInfoItems,
                      auctionInfoItems,
                      imageUrls,
                      translate,
                    );
                  } catch (err) {
                    console.error("PDF download failed", err);
                  } finally {
                    setDownloadingPdf(false);
                  }
                }}
                className={`flex items-center gap-1.5 text-xs font-bold transition-colors uppercase tracking-wider bg-soft px-3 py-1.5 rounded-md border ${downloadingPdf
                  ? "text-gray-400 border-gray-400/20 cursor-not-allowed"
                  : "text-primary hover:text-white border-primary/20"
                  }`}
              >
                <Download
                  size={14}
                  className={downloadingPdf ? "animate-bounce" : ""}
                />
                <span>
                  {downloadingPdf
                    ? translate("vehicleDetail.downloading") || "Downloading..."
                    : translate("vehicleDetail.downloadPdf") || "Download PDF"}
                </span>
              </button>
            </div>

            {/* Grid Content */}
            <div className="p-4">
              <ul className="grid grid-cols-1 md:grid-cols-3 gap-y-6 gap-x-4">
                {carInfoItems.map(
                  ([label, value, isHighlight, valueClassName], idx) => (
                    <li key={idx} className="flex items-center gap-3">
                      {/* SVG Icon Container */}
                      {infoIcons[label] && (
                        <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-soft flex items-center justify-center text-primary">
                          {infoIcons[label]}
                        </div>
                      )}

                      {/* Label and Value Stack */}
                      <div className="flex flex-col min-w-0">
                        <span className="text-[10px] leading-tight text-description font-medium uppercase">
                          {label}
                        </span>
                        <span
                          className={`text-[12px] font-bold truncate ${isHighlight
                            ? "bg-[#3D923A] text-white px-2 py-0.5 rounded-full mt-0.5 w-fit"
                            : valueClassName || "text-white"
                            }`}
                        >
                          {value || "-"}
                        </span>
                      </div>
                    </li>
                  ),
                )}
              </ul>
            </div>
          </div>

          {/* Vehicle Description Card */}
          {(carDetail?.description && (
            <div className="bg-card border border-default rounded-lg shadow-md overflow-hidden lg:mb-8">
              <div className="px-4 py-3 border-b border-default">
                <h2 className="text-white font-semibold text-sm uppercase tracking-wider">
                  {translate("vehicleDetail.vehicleDescription")}
                </h2>
              </div>
              <div className="p-4 pb-5 text-description text-sm leading-relaxed">
                <p>
                  {carDetail?.description ||
                    translate("vehicleDetail.noVehicleDetails")}
                </p>
              </div>
            </div>
          ))}

          {/* Car Information Card */}
          {/* <div className="border p-4 shadow-md bg-card">
            {/* <SectionHeader title="Detailed Specifications" icon={carIcon} /> 
            <SectionHeader title="Detailed Specifications" />
            {/* Grid layout: 3 columns per row 
            <ul className="grid grid-cols-3 gap-4">
              {carInfoItems.map(
                ([label, value, isHighlight, valueClassName], idx) => (
                  <li key={idx} className="flex items-start gap-2">
                    {/* Icon on left *
                    {infoIcons[label] && (
                      <span className="flex-shrink-0">{infoIcons[label]}</span>
                    )}

                    {/* Label and value stacked 
                    <div className="flex flex-col">
                      <span className="text-xs font-semibold text-description">{label}</span>
                      <span
                        className={`text-xs ${isHighlight
                            ? "bg-[#3D923A] text-white px-2 py-1 rounded-full"
                            : valueClassName || "font-normal text-white"
                          }`}
                      >
                        {value}
                      </span>
                    </div>
                  </li>
                )
              )}
            </ul>

            {/* Specification Section (Example SVG + text) 
            {carDetail.specification && (
              <div className="flex items-center gap-2 mt-4">
                <svg
                  width="9"
                  height="19"
                  viewBox="0 0 9 19"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M0.608398 0.885864H8.8584V18.8859H0.608398V0.885864Z"
                    fill="#FF0000"
                  />
                </svg>
                <div className="text-[14px]">{carDetail.specification}</div>
              </div>
            )}
          </div> */}
        </div>

        {/* Right Side: Bid Information & Auction Information */}
        {/* <div className="lg:col-span-1 flex flex-col gap-5 p-2"> */}
        <div className="lg:col-span-4 flex flex-col gap-4 lg:gap-5 p-0 lg:p-2 mt-4 lg:mt-0">
          {/* Bid Info */}
          <BidInformation
            vehicle={carDetail}
            prebidTimeLeft={prebidTimeLeft}
            bidAmount={bidAmount}
            setBidAmount={setBidAmount}
            bidIncrement={bidIncrement}
            bidMessage={bidMessage}
            isSubmittingBid={isSubmittingBid}
            handlePreBidSubmit={handlePreBidSubmit}
            handleBuyNowSubmit={handleBuyNowSubmit}
            getMinBidAmount={getMinBidAmount}
            location={
              carDetail?.auctionLanesVehicles?.[0]?.auctionLane?.auction?.branch
                ?.name ||
              auctionInfo.saleLocation ||
              ""
            }
          />

          {/* Auction Info Card */}
          {/* <div className="rounded-xl p-4 shadow-md bg-card">
            {/* <SectionHeader title="Auction Information" icon={auctionIcon} /> 
            <ul className="text-sm flex flex-col gap-4 pb-5">
              {auctionInfoItems.map(([label, value], idx) => (
                <li key={idx} className="flex justify-between">
                  <span className="text-description text-[14px] font-semibold">
                    {label}:
                  </span>
                  <span>
                    {(!carDetail?.auctionLanesVehicles?.[0]?.auctionLane?.id ||
                      carDetail?.auctionLanesVehicles?.[0]?.auctionLane?.id === 0)
                      ? (label === "Auction Date" || label === "Auction Time" ? "Future" : "-")
                      : value}
                  </span>
                </li>
              ))}
            </ul>
          </div> */}

          {/* Auction Info Card  new sky-auto trasde*/}
          <div className="rounded-xl p-5 shadow-md bg-card border border-default w-full">
            {/* Header: Small title and Auction Name */}
            <div className="">
              <h3 className="text-description text-[10px] uppercase font-bold tracking-wider mb-4">
                {translate("vehicleDetail.auctionDetails")}
              </h3>
              <p className="text-white text-[16px] font-bold">
                {carDetail?.auctionLanesVehicles?.[0]?.auctionLane?.auction
                  ?.name || "-"}
              </p>
            </div>

            {/* Date & Time Row */}
            <div className="flex items-center gap-2 text-primary mb-4 text-[14px] font-semibold">
              <Calendar size={14} />
              <span>
                {carDetail?.auctionLanesVehicles?.[0]?.auctionLane?.auction
                  ?.startDate
                  ? `${new Date(carDetail.auctionLanesVehicles[0].auctionLane.auction.startDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })} - ${new Date(carDetail.auctionLanesVehicles[0].auctionLane.auction.startDate).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: true })} EST`
                  : "--"}
              </span>
            </div>

            <hr className="border-white/10 my-4" />

            {/* Time Remaining Row */}
            <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
              <div className="flex items-center gap-2 text-description">
                <Timer size={18} />
                <span className="text-[12px] font-medium">
                  {translate("vehicleDetail.timeRemaining")}
                </span>
              </div>
              <div className="text-white font-bold text-[12px]">
                {translate("vehicleDetail.endsin")}
                <span className="ml-1">
                  {prebidDays}d : {prebidHours}h : {prebidMinutes}m
                </span>
              </div>
            </div>

            {/* Location Row */}
            <div className="flex flex-wrap justify-between items-center gap-2 mb-3">
              <div className="flex items-center gap-2 text-description">
                <MapPin size={18} />
                <span className="text-[12px] font-medium">
                  {translate("vehicleDetail.location")}
                </span>
              </div>
              <span className="text-white font-semibold text-[12px]">
                {carDetail?.auctionLanesVehicles?.[0]?.auctionLane?.auction?.location?.name
                  || "-"}
              </span>
            </div>

            {/* Total Vehicle Row */}
            <div className="flex flex-wrap justify-between items-center gap-2 mb-4">
              <div className="flex items-center gap-2 text-description">
                <Car size={18} />
                <span className="text-[12px] font-medium">
                  {translate("vehicleDetail.totalVehicles")}
                </span>
              </div>
              <span className="text-[12px] font-semibold text-description">
                {totalAuctionVehicles || carDetail?.totalVehicles || "--"}
              </span>
            </div>

            <hr className="border-white/10 my-4" />

            {/* Footer Features */}
            <div className="flex flex-col gap-0 mb-3">
              {" "}
              {/* Changed gap-1 to gap-0 */}
              <div className="flex items-center gap-1 text-white text-sm font-medium">
                <div className="p-1 rounded-full">
                  {" "}
                  {/* Reduced p-1.5 to p-1 */}
                  <ShieldCheck size={16} className="text-green-500" />
                </div>
                <span className="text-green-500 text-[12px]">
                  {translate("vehicleDetail.verifiedListing")}
                </span>
              </div>
              <div className="flex items-center gap-1 text-white text-sm font-medium">
                <div className="p-1 rounded-full">
                  {" "}
                  {/* Reduced p-1.5 to p-1 */}
                  <CircleDollarSign size={16} className="text-green-500" />
                </div>
                <span className="text-green-500 text-[12px]">
                  {translate("vehicleDetail.lessBuyerFees")}
                </span>
              </div>
            </div>
            <button
              onClick={() =>
                handleViewCatalog(
                  carDetail?.auctionLanesVehicles?.[0]?.auctionLane
                )
              } className="w-full py-3 px-4 bg-soft border text-[12px] border-primary/20 text-white font-bold rounded-lg hover:brightness-110 transition-all text-sm uppercase tracking-wide">
              {translate("vehicleDetail.viewAuctionCatalog")}
            </button>
          </div>
          {/* -----------------------------------------auction info card end -----------------------------------------------*/}

          {/* <div className="mx-auto max-w-7xl mt-2">
            <div className="">
              <div className="mb-2 flex items-center gap-x-1">
                <svg
                  width="27"
                  height="27"
                  viewBox="0 0 33 34"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M30.0076 16.9935C30.0076 24.5146 23.9106 30.6116 16.3895 30.6116C8.8685 30.6116 2.77148 24.5146 2.77148 16.9935C2.77148 9.4725 8.8685 3.37549 16.3895 3.37549C23.9106 3.37549 30.0076 9.4725 30.0076 16.9935Z"
                    fill="white"
                  />
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M16.3895 10.525C16.9536 10.525 17.4109 10.9822 17.4109 11.5463V16.5705L20.5163 19.6759C20.9151 20.0747 20.9151 20.7214 20.5163 21.1203C20.1174 21.5191 19.4707 21.5191 19.0719 21.1203L15.6673 17.7158C15.4758 17.5242 15.3682 17.2644 15.3682 16.9935V11.5463C15.3682 10.9822 15.8255 10.525 16.3895 10.525Z"
                    fill="#F15A29"
                  />
                  <path
                    d="M30.0076 16.9935C30.0076 24.5146 23.9106 30.6116 16.3895 30.6116C8.8685 30.6116 2.77148 24.5146 2.77148 16.9935C2.77148 9.4725 8.8685 3.37549 16.3895 3.37549C23.9106 3.37549 30.0076 9.4725 30.0076 16.9935Z"
                    stroke="#F15A29"
                    strokeWidth="1.02135"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <h1 className="text-[20px] font-[500]">
                  <span className="text-[#DE542E] font-[400]">
                    Auction{" "}
                  </span>
                  {(!carDetail?.auctionLanesVehicles?.[0]?.auctionLane?.id || carDetail?.auctionLanesVehicles?.[0]?.auctionLane?.id === 0)
                    ? "Future"
                    : "starts in"}
                </h1>
              </div>

              {(!carDetail?.auctionLanesVehicles?.[0]?.auctionLane?.id || carDetail?.auctionLanesVehicles?.[0]?.auctionLane?.id === 0) ? (
                <div className="flex justify-center items-center w-full">
                  <div className="text-center py-8">
                    <span className="text-2xl font-medium text-gray-600">
                      Auction details will be available soon
                    </span>
                  </div>
                </div>
              ) : (
                <div className="flex justify-center items-center w-full">
                  <div
                    className="flex justify-between items-center w-full max-w-2xl bg-[#181F2A] rounded-2xl shadow-lg px-2 py-1"
                    style={{
                      boxShadow: "0 4px 16px 0 rgba(0,0,0,0.25)",
                    }}
                  >
                     
                    <div className="flex flex-col items-center flex-1">
                      <span className="text-[2rem] sm:text-[2.5rem] md:text-[2.7rem] font-medium text-white leading-none">
                        {auctionDays}
                      </span>
                      <span className="text-sm font-light mt-2 text-white">
                        DAYS
                      </span>
                    </div>
                    <span className="text-[2rem] sm:text-[2.5rem] md:text-[2.7rem] font-medium text-white px-2">
                      :
                    </span>
                     
                    <div className="flex flex-col items-center flex-1">
                      <span className="text-[2rem] sm:text-[2.5rem] md:text-[2.7rem] font-medium text-white leading-none">
                        {auctionHours}
                      </span>
                      <span className="text-sm font-light mt-2 text-white">
                        HOURS
                      </span>
                    </div>
                    <span className="text-[2rem] sm:text-[2.5rem] md:text-[2.7rem] font-medium text-white px-2">
                      :
                    </span>
                    
                    <div className="flex flex-col items-center flex-1">
                      <span className="text-[2rem] sm:text-[2.5rem] md:text-[2.7rem] font-medium text-white leading-none">
                        {auctionMinutes}
                      </span>
                      <span className="text-sm font-light mt-2 text-white">
                        MINUTES
                      </span>
                    </div>
                    <span className="text-[2rem] sm:text-[2.5rem] md:text-[2.7rem] font-medium text-white px-2">
                      :
                    </span>
                    
                    <div className="flex flex-col items-center flex-1">
                      <span className="text-[2rem] sm:text-[2.5rem] md:text-[2.7rem] font-medium text-white leading-none">
                        {auctionSeconds}
                      </span>
                      <span className="text-sm font-light mt-2 text-white">
                        SECONDS
                      </span>
                    </div>
            </div>
          </div> */}
        </div>
      </div>

      {/* car specifications cards  */}
      {/* <div className="mx-auto max-w-7xl grid grid-cols-2 md:grid-cols-6 gap-3 mt-14 text-[#626262] mt-4">
        {carDetail?.mileage && (
          <div className="w-auto h-[140px] flex flex-col items-center bg-white border border-[#D6D6D6] rounded-lg   py-10">
            {" "}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="38"
              height="38"
              viewBox="0 0 22 18"
              fill="none"
            >
              <path
                d="M21.7839 11.0194C21.7839 13.4529 21.0682 15.6239 19.6368 17.5324C19.4936 17.7233 19.3028 17.8306 19.0642 17.8545C18.8256 17.8784 18.6109 17.8187 18.4201 17.6756C18.2292 17.5324 18.1219 17.3535 18.098 17.1388C18.0741 16.9241 18.1338 16.7213 18.2769 16.5304C19.5175 14.9081 20.1378 13.0712 20.1378 11.0194C20.1378 9.39715 19.7322 7.89415 18.9211 6.51044C18.1099 5.12672 17.0006 4.02929 15.593 3.21815C14.1854 2.40701 12.6705 2.00144 11.0482 2.00144C9.42593 2.00144 7.911 2.40701 6.50343 3.21815C5.09586 4.02929 3.9865 5.12672 3.17536 6.51044C2.36421 7.89415 1.95864 9.39715 1.95864 11.0194C1.95864 13.0712 2.57893 14.9081 3.8195 16.5304C3.96264 16.7213 4.02229 16.9241 3.99843 17.1388C3.97457 17.3535 3.86721 17.5324 3.67636 17.6756C3.4855 17.8187 3.27079 17.8784 3.03221 17.8545C2.79364 17.8306 2.60279 17.7233 2.45964 17.5324C1.02821 15.6239 0.3125 13.4529 0.3125 11.0194C0.3125 9.11086 0.789643 7.33351 1.74393 5.68736C2.69821 4.04122 3.99843 2.74101 5.64457 1.78672C7.29072 0.832438 9.09193 0.355293 11.0482 0.355293C13.0045 0.355293 14.8057 0.832438 16.4519 1.78672C18.098 2.74101 19.3982 4.04122 20.3525 5.68736C21.3068 7.33351 21.7839 9.11086 21.7839 11.0194ZM16.3445 6.00944C16.5354 6.15258 16.6308 6.34344 16.6308 6.58201C16.6308 6.82058 16.5354 7.01144 16.3445 7.15458L13.6248 9.94586C13.8634 10.3753 13.9826 10.8286 13.9826 11.3057C13.9826 12.1169 13.6964 12.8087 13.1238 13.3813C12.5512 13.9539 11.8594 14.2401 11.0482 14.2401C10.2371 14.2401 9.54522 13.9539 8.97264 13.3813C8.40007 12.8087 8.11379 12.1169 8.11379 11.3057C8.11379 10.4946 8.40007 9.80272 8.97264 9.23015C9.54522 8.65758 10.2371 8.37129 11.0482 8.37129C11.5254 8.37129 11.9786 8.49058 12.4081 8.72915L15.1994 6.00944C15.3425 5.81858 15.5334 5.72315 15.7719 5.72315C16.0105 5.72315 16.2014 5.81858 16.3445 6.00944ZM12.3365 11.3057C12.3365 10.9717 12.2053 10.6854 11.9429 10.4469C11.6804 10.2083 11.3822 10.089 11.0482 10.089C10.7142 10.089 10.416 10.2083 10.1536 10.4469C9.89114 10.6854 9.75993 10.9717 9.75993 11.3057C9.75993 11.6397 9.89114 11.9379 10.1536 12.2004C10.416 12.4628 10.7142 12.594 11.0482 12.594C11.3822 12.594 11.6804 12.4628 11.9429 12.2004C12.2053 11.9379 12.3365 11.6397 12.3365 11.3057Z"
                fill="#DE542E"
              />
            </svg>
            <div className="text-[14px]">{carDetail.mileage + " Mi "}</div>
            <div className="text-[10px]">(Not Actual)</div>
          </div>
        )}
        {carDetail?.engine && (
          <div className="w-auto h-[140px] flex flex-col items-center bg-white border border-[#D6D6D6] rounded-lg   py-10">
            {" "}
            <svg
              width="38"
              height="38"
              viewBox="0 0 22 22"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <g clip-path="url(#clip0_1730_33991)">
                <path
                  d="M13.1456 6.40362V4.6354H15.0927V2.86719H9.64062V4.6354H11.5878V6.40362"
                  stroke="#E95923"
                  stroke-width="0.934628"
                  stroke-miterlimit="10"
                  strokeLinejoin="round"
                />
                <path
                  d="M4.96705 12.1496H3.40931V9.49731H1.85156V16.5702H3.40931V13.9179H4.96705"
                  stroke="#E95923"
                  stroke-width="0.934628"
                  stroke-miterlimit="10"
                  strokeLinejoin="round"
                />
                <path
                  d="M18.9865 9.05426V11.2645H17.8182V7.6587C17.8182 7.20913 17.5024 6.84399 17.1137 6.84399H9.2467C9.06016 6.84399 8.88024 6.93019 8.74822 7.08359L6.91398 9.49632H5.74567C5.35623 9.49632 4.9668 9.93837 4.9668 10.3804V15.6851C4.9668 16.1351 5.35701 16.5692 5.74567 16.5692H6.91398L8.749 18.9832C8.88102 19.1357 9.06016 19.2215 9.2467 19.2215H14.2529C14.4394 19.2215 14.6186 19.1357 14.7506 18.9832L17.6114 15.6758C17.7442 15.5228 17.8178 15.316 17.8178 15.0998V13.9169H18.9861V16.1271H20.5439V9.05426H18.9865Z"
                  stroke="#E95923"
                  stroke-width="0.934628"
                  stroke-miterlimit="10"
                  stroke-linecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M13.9237 12.5879L10.8082 17.4505L11.9765 13.4721H10.0293L11.9765 9.05151H13.9237L11.9765 12.5879H13.9237Z"
                  fill="#E95923"
                />
              </g>
              <defs>
                <clipPath id="clip0_1730_33991">
                  <rect
                    width="21.0291"
                    height="21.0291"
                    fill="white"
                    transform="matrix(1 0 0 -1 0.683594 21.5559)"
                  />
                </clipPath>
              </defs>
            </svg>
            <div className="text-[14px]">{carDetail.engine}</div>
          </div>
        )}
        {carDetail?.cylinder && (
          <div className="w-auto h-[140px] flex flex-col items-center bg-white border border-[#D6D6D6] rounded-lg   py-10">
            {" "}
            <svg
              width="38"
              height="38"
              viewBox="0 0 22 22"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M14.6187 1.60034C14.9661 1.54206 15.3765 1.59822 15.7065 1.76245L15.8433 1.8396L15.8442 1.84058C15.8639 1.85357 15.8936 1.87829 15.9263 1.90698C15.9616 1.93801 16.0078 1.98008 16.063 2.03101C16.1734 2.13293 16.3232 2.2736 16.5034 2.44604C16.8642 2.79124 17.3495 3.26429 17.896 3.80542L18.896 4.79858C19.1521 5.0552 19.3388 5.24445 19.4761 5.3894C19.7484 5.67706 19.8368 5.79741 19.897 5.92065C20.0743 6.27344 20.1338 6.66199 20.0747 7.01929V7.02026C20.052 7.15075 20.0374 7.26297 19.9849 7.3855C19.932 7.50884 19.8436 7.63774 19.6851 7.82202C19.3696 8.18864 18.7453 8.80779 17.4692 10.0847C16.4484 11.107 15.855 11.6941 15.4917 12.0398C15.1318 12.3823 14.9885 12.4964 14.8696 12.5603L14.8677 12.5613C14.4483 12.7788 14.023 12.8325 13.5972 12.7214H13.5962C13.3863 12.6639 13.2318 12.6038 13.0444 12.4705C12.8625 12.3409 12.6493 12.1423 12.3179 11.8152L11.7661 11.2712L6.95361 16.074L7.01123 16.1892L7.07764 16.3328C7.22022 16.6682 7.27686 17.0109 7.27686 17.4587C7.27684 17.7399 7.2585 17.9587 7.21045 18.1677C7.16237 18.3769 7.08588 18.5714 6.97314 18.8044H6.97217C6.58547 19.6014 5.86111 20.1929 4.98975 20.4226H4.98877C4.56556 20.5305 3.88838 20.5306 3.46631 20.4226H3.46533C1.64925 19.9383 0.687092 17.9582 1.41553 16.2654L1.49072 16.1023C1.57086 15.9382 1.63365 15.8249 1.7124 15.7156C1.79063 15.607 1.88246 15.5053 2.01709 15.363C2.41953 14.9364 2.86556 14.6729 3.43115 14.5095H3.43311C3.63603 14.455 3.92569 14.4248 4.20654 14.4207C4.48624 14.4165 4.77322 14.4375 4.96826 14.4939H4.96729L5.13623 14.5398L5.26416 14.4304C5.36971 14.3343 6.5075 13.2135 7.81494 11.9089L10.1157 9.61304L9.68311 9.17456C9.41768 8.90603 9.24582 8.71824 9.13037 8.54858C9.01192 8.37452 8.95353 8.22237 8.90771 8.03003C8.80575 7.59706 8.88976 7.06841 9.11084 6.69507L9.11279 6.69214H9.11377C9.12747 6.67072 9.15917 6.63441 9.19385 6.59644C9.23238 6.55425 9.28491 6.49779 9.3501 6.42944C9.48102 6.29218 9.66361 6.10437 9.88428 5.87964C10.326 5.42976 10.9227 4.83028 11.5679 4.19019C12.639 3.12504 13.2299 2.5396 13.5786 2.20972C13.9207 1.8861 14.0416 1.79626 14.1655 1.74292L14.3921 1.65601C14.4737 1.62966 14.5559 1.60832 14.6187 1.59937V1.60034ZM4.93994 15.4275C4.30037 15.2101 3.60179 15.2937 3.05615 15.6589H3.05518C2.92841 15.7425 2.77505 15.8831 2.63623 16.0388C2.4973 16.1947 2.3802 16.357 2.31982 16.4812V16.4822C1.83075 17.4722 2.139 18.6249 3.05615 19.24H3.05518C3.2503 19.3701 3.41811 19.4526 3.59814 19.5037C3.77879 19.5549 3.97868 19.5759 4.2417 19.5759C4.50448 19.5759 4.70451 19.555 4.88525 19.5037C5.06542 19.4525 5.23273 19.3695 5.42725 19.24C6.04539 18.8184 6.38916 18.1902 6.38916 17.4568C6.38906 16.8932 6.20075 16.4008 5.82666 16.0046C5.55389 15.7185 5.29198 15.5473 4.93994 15.4275ZM8.39307 12.6101C7.72024 13.2815 7.10872 13.8935 6.66553 14.3406C6.444 14.5641 6.26488 14.7462 6.14111 14.8738C6.09847 14.9177 6.06227 14.955 6.03369 14.9851C6.04295 14.9924 6.05284 15.0002 6.06299 15.0085C6.11571 15.052 6.18368 15.1117 6.25244 15.1765H6.25146L6.42041 15.323L8.78076 12.9666L11.1226 10.6169L10.9448 10.4392L10.7573 10.2576L8.39307 12.6101ZM12.3062 4.74487C12.2395 4.80941 12.1431 4.90294 12.0259 5.01831C11.7916 5.24895 11.4715 5.56659 11.1235 5.91382C10.5165 6.51901 10.2004 6.83637 10.0288 7.02612C9.94313 7.12088 9.89762 7.17911 9.87061 7.22241C9.84598 7.2619 9.83448 7.29051 9.82275 7.33081L9.82373 7.33179C9.78917 7.45351 9.77397 7.53684 9.77393 7.61597C9.77393 7.69517 9.78914 7.7783 9.82373 7.90015H9.82275C9.83407 7.9384 9.84532 7.96671 9.87842 8.01538C9.91502 8.06915 9.97895 8.14639 10.1001 8.2771C10.3417 8.53773 10.7946 8.99418 11.6704 9.87183L12.8384 11.0349C13.1261 11.3165 13.3255 11.5032 13.4751 11.6267C13.6236 11.7493 13.714 11.8013 13.7856 11.8259C13.8555 11.8499 13.9161 11.8503 14.0239 11.8503C14.1315 11.8482 14.1987 11.8457 14.2661 11.8259C14.3321 11.8066 14.4069 11.7687 14.5171 11.6843C14.7428 11.5116 15.0879 11.1697 15.7798 10.4792L16.938 9.32202L14.6392 7.0271H14.6382C13.9856 6.37172 13.3887 5.77681 12.9546 5.34546C12.7376 5.1299 12.561 4.95514 12.439 4.83374C12.3944 4.78943 12.3572 4.75198 12.3276 4.72241C12.3206 4.72918 12.314 4.73725 12.3062 4.74487ZM12.9722 4.06909L17.5874 8.67456L18.1509 8.11304L16.1831 6.15796V6.15894C15.0507 5.03641 13.9749 3.96353 13.7866 3.78296L13.7856 3.78198L13.519 3.52222L12.9722 4.06909ZM15.2876 2.56226C14.9587 2.39873 14.5383 2.48374 14.2524 2.79077L14.2495 2.79468L14.1665 2.87671L18.7993 7.46948L18.8921 7.38159C19.0032 7.27467 19.0759 7.17604 19.1216 7.07397C19.167 6.97236 19.188 6.86043 19.188 6.72339C19.188 6.61077 19.1883 6.55009 19.1655 6.4812C19.1427 6.41196 19.0929 6.32561 18.9731 6.1814C18.7317 5.89078 18.2413 5.40673 17.2368 4.41284V4.41187C16.5676 3.74887 16.105 3.29596 15.7944 3.00171C15.6391 2.85449 15.5226 2.74803 15.438 2.67554C15.3959 2.63947 15.363 2.61239 15.3374 2.59351C15.3246 2.58408 15.314 2.57695 15.3062 2.57202C15.3026 2.56978 15.2996 2.56835 15.2974 2.56714C15.2962 2.56653 15.295 2.56549 15.2944 2.56519H15.2935L15.2876 2.56226Z"
                fill="#E95923"
                stroke="#E95923"
                stroke-width="0.233657"
                strokeLinejoin="round"
              />
              <path
                d="M12.9655 7.92444C12.7354 8.02765 12.5454 8.31201 12.5454 8.53213C12.5454 8.77647 12.7586 9.06083 13.0045 9.15561C13.5587 9.36836 14.0811 8.78489 13.8363 8.23197C13.6853 7.92444 13.2737 7.77384 12.9655 7.92444Z"
                fill="#E95923"
              />
            </svg>
            <div className="text-[14px]">{carDetail.cylinder}</div>
          </div>
        )}
        {carDetail?.fuelType && (
          <div className="w-auto h-[140px] flex flex-col items-center bg-white border border-[#D6D6D6] rounded-lg   py-10">
            {" "}
            <svg
              width="38"
              height="38"
              viewBox="0 0 21 22"
              fill="#E95923"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12.2732 4.45221C12.2732 4.26529 12.2148 4.11341 12.0979 3.99658C11.9811 3.87975 11.8292 3.82134 11.6423 3.82134H5.05317C4.86624 3.82134 4.71436 3.87975 4.59754 3.99658C4.48071 4.11341 4.42229 4.26529 4.42229 4.45221V9.7095C4.42229 9.89642 4.48071 10.06 4.59754 10.2002C4.71436 10.3404 4.86624 10.4105 5.05317 10.4105H11.6423C11.8292 10.4105 11.9811 10.3404 12.0979 10.2002C12.2148 10.06 12.2732 9.89642 12.2732 9.7095V4.45221ZM10.9413 9.07862H5.68404V5.15318H10.9413V9.07862ZM19.8437 5.15318L17.18 3.89143C17.0398 3.79797 16.8762 3.77461 16.6893 3.82134C16.5024 3.86807 16.3739 3.97322 16.3038 4.13677C16.2337 4.30033 16.222 4.47558 16.2687 4.6625C16.3154 4.84943 16.4323 4.96626 16.6192 5.01299L17.6005 5.57376L17.5305 5.78406C17.5305 6.20464 17.659 6.57849 17.916 6.90561C18.173 7.23273 18.4884 7.46639 18.8623 7.60658V15.5977C18.8623 15.7846 18.7922 15.9481 18.652 16.0883C18.5118 16.2285 18.3599 16.2986 18.1964 16.2986C18.0328 16.2986 17.8809 16.2285 17.7407 16.0883C17.6005 15.9481 17.5305 15.7846 17.5305 15.5977V10.3404C17.5305 9.59267 17.2617 8.91506 16.7243 8.30755C16.1869 7.70004 15.5911 7.32619 14.9369 7.186V3.12037C14.9369 2.4194 14.6798 1.81189 14.1658 1.29784C13.6517 0.783796 13.0442 0.526773 12.3433 0.526773H4.42229C3.72132 0.526773 3.10213 0.783796 2.56472 1.29784C2.02731 1.81189 1.7586 2.4194 1.7586 3.12037V17.8408L0.84734 18.3314C0.566952 18.4716 0.426758 18.6586 0.426758 18.8922V20.8549C0.426758 21.0419 0.496855 21.2054 0.637049 21.3456C0.777243 21.4858 0.940803 21.5559 1.12773 21.5559H15.5677C15.7547 21.5559 15.9065 21.4858 16.0234 21.3456C16.1402 21.2054 16.1986 21.0419 16.1986 20.8549V18.8922C16.1986 18.6586 16.0818 18.4716 15.8481 18.3314L14.9369 17.8408V8.51784C15.264 8.65804 15.5561 8.90338 15.8131 9.25386C16.0701 9.60435 16.1986 9.96652 16.1986 10.3404V15.5977C16.1986 16.1584 16.3972 16.6374 16.7944 17.0346C17.1917 17.4319 17.659 17.6305 18.1964 17.6305C18.7338 17.6305 19.2011 17.4319 19.5983 17.0346C19.9955 16.6374 20.1941 16.1584 20.1941 15.5977V5.78406C20.1941 5.50367 20.0773 5.29338 19.8437 5.15318ZM14.9369 20.2241H1.7586V19.3128L2.73996 18.8221C2.97362 18.7287 3.09045 18.5417 3.09045 18.2613V3.12037C3.09045 2.79325 3.21896 2.50117 3.47598 2.24415C3.733 1.98713 4.04844 1.85862 4.42229 1.85862H12.3433C12.6704 1.85862 12.9625 1.98713 13.2195 2.24415C13.4765 2.50117 13.605 2.79325 13.605 3.12037V18.2613C13.605 18.5417 13.7218 18.7287 13.9555 18.8221L14.9369 19.3128V20.2241Z"
                fill="#E95923"
              />
            </svg>
            <div className="text-[14px]">{carDetail.fuelType}</div>
          </div>
        )}

        {carDetail?.TransmissionType && (
          <div className="w-auto h-[140px] flex flex-col items-center bg-white border border-[#D6D6D6] rounded-lg   py-10">
            {" "}
            <svg
              width="38"
              height="38"
              viewBox="0 0 37 37"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M11.1787 8.80576C11.1787 10.1975 10.0504 11.3257 8.65866 11.3257C7.26691 11.3257 6.13867 10.1975 6.13867 8.80576C6.13867 7.414 7.26691 6.28577 8.65866 6.28577C10.0504 6.28577 11.1787 7.414 11.1787 8.80576Z"
                stroke="#e95923"
                stroke-width="2"
              />
              <path
                d="M11.1787 28.9658C11.1787 30.3575 10.0504 31.4858 8.65866 31.4858C7.26691 31.4858 6.13867 30.3575 6.13867 28.9658C6.13867 27.574 7.26691 26.4458 8.65866 26.4458C10.0504 26.4458 11.1787 27.574 11.1787 28.9658Z"
                stroke="#e95923"
                stroke-width="2"
              />
              <path
                d="M21.2578 28.9658C21.2578 30.3575 20.1295 31.4858 18.7378 31.4858C17.346 31.4858 16.2178 30.3575 16.2178 28.9658C16.2178 27.574 17.346 26.4458 18.7378 26.4458C20.1295 26.4458 21.2578 27.574 21.2578 28.9658Z"
                stroke="#e95923"
                stroke-width="2"
              />
              <path
                d="M21.2578 8.80588C21.2578 10.1976 20.1295 11.3259 18.7378 11.3259C17.346 11.3259 16.2178 10.1976 16.2178 8.80588C16.2178 7.41413 17.346 6.28589 18.7378 6.28589C20.1295 6.28589 21.2578 7.41413 21.2578 8.80588Z"
                stroke="#e95923"
                stroke-width="2"
              />
              <path
                d="M31.3378 8.80588C31.3378 10.1976 30.2096 11.3259 28.8178 11.3259C27.4261 11.3259 26.2979 10.1976 26.2979 8.80588C26.2979 7.41413 27.4261 6.28589 28.8178 6.28589C30.2096 6.28589 31.3378 7.41413 31.3378 8.80588Z"
                stroke="#e95923"
                stroke-width="2"
              />
              <path
                d="M8.6582 11.3259V26.4459"
                stroke="#e95923"
                stroke-width="2"
                stroke-linecap="round"
              />
              <path
                d="M18.7383 11.3259V26.4459"
                stroke="#e95923"
                stroke-width="2"
                stroke-linecap="round"
              />
              <path
                d="M28.8181 11.3259V13.8459C28.8181 16.2218 28.8181 17.4097 28.08 18.1478C27.3419 18.8859 26.154 18.8859 23.7781 18.8859H8.6582"
                stroke="#e95923"
                stroke-width="2"
                stroke-linecap="round"
              />
              <path
                d="M26.2979 22.6659V22.0659C25.9665 22.0659 25.6979 22.3345 25.6979 22.6659H26.2979ZM25.6979 31.4859C25.6979 31.8172 25.9665 32.0859 26.2979 32.0859C26.6292 32.0859 26.8979 31.8172 26.8979 31.4859H26.2979H25.6979ZM30.8261 31.7992C30.9991 32.0818 31.3685 32.1706 31.6511 31.9976C31.9337 31.8245 32.0226 31.4552 31.8495 31.1726L31.3378 31.4859L30.8261 31.7992ZM26.2979 22.6659V23.2659H29.1778V22.6659V22.0659H26.2979V22.6659ZM26.2979 27.0759H26.8979V22.6659H26.2979H25.6979V27.0759H26.2979ZM31.3378 24.8709H30.7378C30.7378 25.769 30.0278 26.4759 29.1778 26.4759V27.0759V27.6759C30.7137 27.6759 31.9378 26.4083 31.9378 24.8709H31.3378ZM29.1778 22.6659V23.2659C30.0278 23.2659 30.7378 23.9727 30.7378 24.8709H31.3378H31.9378C31.9378 23.3335 30.7137 22.0659 29.1778 22.0659V22.6659ZM29.1778 27.0759V26.4759H28.6378V27.0759V27.6759H29.1778V27.0759ZM28.6378 27.0759V26.4759H26.2979V27.0759V27.6759H28.6378V27.0759ZM28.6378 27.0759L28.1261 27.3892L30.8261 31.7992L31.3378 31.4859L31.8495 31.1726L29.1496 26.7626L28.6378 27.0759ZM26.2979 27.0759H25.6979V31.4859H26.2979H26.8979V27.0759H26.2979Z"
                fill="#e95923"
              />
            </svg>
            <div className="text-[14px]">{carDetail.TransmissionType}</div>
          </div>
        )}
        {carDetail?.specification && (
          <div className="w-auto h-[140px] flex flex-col items-center bg-white border border-[#D6D6D6] rounded-lg   py-10">
            {" "}
            <svg
              width="38"
              height="38"
              viewBox="0 0 25 19"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0.608398 0.885864H24.6084V6.88586H0.608398V0.885864Z"
                fill="#00732F"
              />
              <path
                d="M0.608398 6.88586H24.6084V12.8859H0.608398V6.88586Z"
                fill="white"
              />
              <path
                d="M0.608398 12.8859H24.6084V18.8859H0.608398V12.8859Z"
                fill="#131313"
              />
              <path
                d="M0.608398 0.885864H8.8584V18.8859H0.608398V0.885864Z"
                fill="#FF0000"
              />
            </svg>
            <div className="text-[14px]">{carDetail.specification}</div>
          </div>
        )}
      </div>  */}

      {/* car specifications cards end  */}

      {/* Image Gallery Modal */}
      {showGallery && (
        <div className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center">
          {/* Close Button */}
          <button
            onClick={closeGallery}
            className="absolute top-4 right-4 text-white hover:text-primary transition-colors z-50"
            aria-label="Close gallery"
          >
            <X size={32} />
          </button>

          {/* Download All Button */}
          {/* <button
            onClick={downloadAllImages}
            className="absolute top-4 left-4 bg-primary text-white px-4 py-2 rounded-lg hover:bg-primary-dark transition-all flex items-center gap-2 z-50"
          >
            <Download size={18} />
            <span className="text-sm font-semibold">Download All</span>
          </button> */}

          {/* Image Counter */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-lg z-50">
            <span className="text-sm font-semibold">
              {currentGalleryIndex + 1} / {imageUrls.length}
            </span>
          </div>

          {/* Main Image Container */}
          <div className="relative w-full h-full flex items-center justify-center p-20">
            <img
              src={imageUrls[currentGalleryIndex]}
              alt={`Vehicle image ${currentGalleryIndex + 1}`}
              className="max-w-full max-h-full object-contain rounded-lg"
            />

            {/* Previous Button */}
            {imageUrls.length > 1 && (
              <button
                onClick={previousImage}
                className="absolute left-8 top-1/2 -translate-y-1/2 bg-black/70 hover:bg-black/90 text-white p-4 rounded-full transition-all hover:scale-110"
                aria-label="Previous image"
              >
                <ChevronLeft size={32} />
              </button>
            )}

            {/* Next Button */}
            {imageUrls.length > 1 && (
              <button
                onClick={nextImage}
                className="absolute right-8 top-1/2 -translate-y-1/2 bg-black/70 hover:bg-black/90 text-white p-4 rounded-full transition-all hover:scale-110"
                aria-label="Next image"
              >
                <ChevronRight size={32} />
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default VehicleDetail;
